import { gql, useMutation } from '@apollo/client';
import { Center, IconButton, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { logError } from '@/imports/logging/ClientLogger';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { PlusIcon } from '@/imports/ui/chakra/feather';

import {
  AddMemberButton_CreateGroupLink,
  AddMemberButton_CreateGroupLinkVariables,
} from './graphql-types/AddMemberButton_CreateGroupLink';

const CREATE_GROUP_LINK = gql`
  mutation AddMemberButton_CreateGroupLink($input: CreateUserGroupLinkInput!) {
    createUserGroupLink(input: $input) {
      data {
        _id
        userId
        groupId
      }
    }
  }
`;

type IAddMemberButtonProps = {
  groupId: string;
  userId: string;
};

export const AddMemberButton: React.FC<IAddMemberButtonProps> = ({
  groupId,
  userId,
}) => {
  const toast = useToast();
  const [createGroupLinkFunc, { loading }] = useMutation<
    AddMemberButton_CreateGroupLink,
    AddMemberButton_CreateGroupLinkVariables
  >(CREATE_GROUP_LINK, {
    refetchQueries: [
      'GroupAddMembersModal_GroupUsersSearch',
      'GroupMembers_GroupUsersSearch',
    ],
    awaitRefetchQueries: true,
  });

  const addToGroupHandler = async (): Promise<void> => {
    try {
      await createGroupLinkFunc({
        variables: {
          input: {
            data: {
              userId,
              groupId,
            },
          },
        },
      });
      toast({ title: 'Added member to group.', status: 'success' });
    } catch (err) {
      toast({ title: 'The user cannot be added because they previously opted out of the group. Please impersonate the user to add them back to the group.', status: 'error' });
      logError(err);
    }
  };

  if (loading) {
    return (
      <Center h="10" w="10">
        <Loading />
      </Center>
    );
  }

  return (
    <IconButton
      aria-label="Add member to group"
      colorScheme="gray"
      icon={<PlusIcon />}
      onClick={addToGroupHandler}
    />
  );
};
